/**
 * PlanningMobileShell — phone frame for the Planning app.
 *
 * Same rollback-safe pattern as MobileShell for the Prioritization app:
 *   - Injected by a single conditional in PlanningLayout.tsx
 *   - Isolated entirely under src/mobile/**
 *   - Kill-switches are identical (useMobileExperience())
 *   - No quick capture (Planning has no intake flow)
 *   - No Focus overlay (Planning is reference/tracking, not task-execution)
 *
 * Reuses all shared primitives: BottomSheet, MobileTabBar, MobileDrawer.
 * The only planning-specific pieces are the nav config and header.
 *
 * One page per address (roadmap M-7, 2026-10-29): it renders the routed main
 * Planning pages inside the phone frame. The separate phone copies (home,
 * plans, plan, work graph, milestones, capacity, Plan of Record, governance,
 * assumptions & risks, changes, people & capacity, areas, workbench, launch
 * calendar, the engine) were removed.
 */
import { type ReactNode, useState } from 'react';
import './mobile.css';
import { ToastProvider } from '../components/ui/Toast';
import { NotificationToast } from '../components/NotificationToast';
import { CommandPalette } from '../components/prioritization/CommandPalette';
import { MobilePlanningHeader } from './MobilePlanningHeader';
import { MobileTabBar } from './MobileTabBar';
import { MobileTabSubmenu } from './MobileTabSubmenu';
import { MobileDrawer } from './MobileDrawer';
import { activePlanningTabHref } from './planningNavConfig';
import { usePlanningMobileNav } from './usePlanningMobileNav';
import { isPlanningTopLevelRoute } from './mobilePlanningRoutes';
import { useLocation } from 'react-router-dom';
import type { MobileNavItem } from './navConfig';

interface PlanningMobileShellProps {
  children: ReactNode;
}

export function PlanningMobileShell({ children }: PlanningMobileShellProps) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [tabSubmenu, setTabSubmenu] = useState<MobileNavItem | null>(null);

  const { tabItems, drawerSections } = usePlanningMobileNav();
  const activeHref = drawerOpen ? null : activePlanningTabHref(location.pathname);
  // The header is on every page: the bell and theme live in it (audit #35).
  const topLevel = isPlanningTopLevelRoute(location.pathname);
  const mainPaddingTop = 'calc(var(--pa-header-h) + var(--pa-safe-top))';

  return (
    <ToastProvider>
      <div className="pa-mobile min-h-screen bg-white dark:bg-[var(--bg)] text-gray-900 dark:text-[var(--text-pri)]">
        <NotificationToast />
        <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />

        <MobilePlanningHeader topLevel={topLevel} />

        <main
          className="pa-scroll"
          style={{
            paddingTop: mainPaddingTop,
            paddingBottom: 'calc(var(--pa-tabbar-h) + var(--pa-safe-bottom))',
            minHeight: '100vh',
          }}
        >
          {/* The phone's version of the desktop frame's page gutter. */}
          <div className="px-4 pt-3 pb-4 min-w-0">{children}</div>
        </main>

        <MobileTabBar
          items={tabItems}
          activeHref={activeHref}
          onMore={() => {
            setTabSubmenu(null);
            setDrawerOpen((open) => !open);
          }}
          onTabSubmenu={(tab) => {
            setDrawerOpen(false);
            setTabSubmenu(tab);
          }}
          moreOpen={drawerOpen}
        />

        <MobileTabSubmenu
          open={tabSubmenu != null}
          onClose={() => setTabSubmenu(null)}
          tab={tabSubmenu}
        />

        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sections={drawerSections}
          onSearch={() => setSearchOpen(true)}
        />
      </div>
    </ToastProvider>
  );
}
